import {
    createSlice
} from "@reduxjs/toolkit";
import {
    createAd,
    getAdPlan,
    getAllProfiles,
    getAnalyticsCount,
    getLiveAds,
    updateAd,
    getBannerAds
} from "./thunk";
export const initialState = {
    data:[],
    profiles:[],
    adPlans: [],
    analyticsCount: [],
    bannerAds: [],
    error: "", // for error message
    loading: false,
    // Profile data caching
    profileCache: {
        data: [],
        total: 0,
        totalPages: 0,
        currentPage: 1,
        hasMore: true,
        lastFetched: null,
        filters: null, // API filter format
        filterState: { // Component filter state
            filterOption: '',
            filterValue: ''
        },
        sort: {
            value: ''
        }
    },
    // Dashboard ads cache
    dashboardAdsCache: {
        data: [],
        lastFetched: null
    }
};

const adsSlice = createSlice({
    name: "ads",
    initialState,
    reducers: {
        // Profile cache actions
        updateProfileCache: (state, action) => {
            const { data, total, totalPages, page, hasMore, filters, filterState, sort } = action.payload;
            
            // Ensure data is an array
            const profilesData = Array.isArray(data) ? data : [];
            
            if (page === 1) {
                state.profileCache.data = profilesData;
            } else {
                // Only add new profiles that don't already exist
                const existingIds = new Set(state.profileCache.data.map(profile => profile.id));
                const newProfiles = profilesData.filter(profile => !existingIds.has(profile.id));
                state.profileCache.data = [...state.profileCache.data, ...newProfiles];
            }
            
            state.profileCache.total = total || 0;
            state.profileCache.totalPages = totalPages || 0;
            state.profileCache.currentPage = page || 1;
            state.profileCache.hasMore = hasMore !== undefined ? hasMore : false;
            state.profileCache.lastFetched = Date.now();
            if (filters !== undefined) state.profileCache.filters = filters;
            if (filterState !== undefined) state.profileCache.filterState = filterState;
            if (sort) state.profileCache.sort = sort;
        },
        clearProfileCache: (state) => {
            state.profileCache = {
                data: [],
                total: 0,
                totalPages: 0,
                currentPage: 1,
                hasMore: true,
                lastFetched: null,
                filters: null,
                filterState: {
                    filterOption: '',
                    filterValue: ''
                },
                sort: {
                    value: ''
                }
            };
        },
        updateDashboardAdsCache: (state, action) => {
            state.dashboardAdsCache.data = action.payload;
            state.dashboardAdsCache.lastFetched = Date.now();
        },
        clearDashboardAdsCache: (state) => {
            state.dashboardAdsCache = {
                data: [],
                lastFetched: null
            };
        },
        updateProfileInCache: (state, action) => {
            const { adId, updatedData } = action.payload;
            const index = state.profileCache.data.findIndex(profile => profile.id === adId);
            if (index !== -1) {
                state.profileCache.data[index] = { ...state.profileCache.data[index], ...updatedData };
            }
        },
        removeProfileFromCache: (state, action) => {
            const adId = action.payload;
            state.profileCache.data = state.profileCache.data.filter(profile => profile.id !== adId);
            state.profileCache.total = Math.max(0, state.profileCache.total - 1);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getLiveAds.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getLiveAds.fulfilled, (state, action) => {
                state.data = action.payload;
                state.loading = false;
            })
            .addCase(getLiveAds.rejected, (state, action) => {
                state.error = action.payload?.error || null;
                state.loading = false;
            })
            .addCase(getAllProfiles.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllProfiles.fulfilled, (state, action) => {
                const { data, total, totalPages, page, hasMore, filters, filterState, sort } = action.payload;
                
                // Debug logging
                console.log('getAllProfiles.fulfilled payload:', action.payload);
                console.log('Data type:', typeof data, 'Is array:', Array.isArray(data));
                
                // Ensure data is an array
                const profilesData = Array.isArray(data) ? data : [];
                
                state.profiles = profilesData;
                
                // Update cache safely
                if (page === 1) {
                    state.profileCache.data = profilesData;
                } else {
                    // Only add new profiles that don't already exist
                    const existingIds = new Set(state.profileCache.data.map(profile => profile.id));
                    const newProfiles = profilesData.filter(profile => !existingIds.has(profile.id));
                    state.profileCache.data = [...state.profileCache.data, ...newProfiles];
                }
                
                state.profileCache.total = total || 0;
                state.profileCache.totalPages = totalPages || 0;
                state.profileCache.currentPage = page || 1;
                state.profileCache.hasMore = hasMore !== undefined ? hasMore : false;
                state.profileCache.lastFetched = Date.now();
                if (filters !== undefined) state.profileCache.filters = filters;
                if (filterState !== undefined) state.profileCache.filterState = filterState;
                if (sort) state.profileCache.sort = sort;
                state.loading = false;
            })
            .addCase(getAllProfiles.rejected, (state, action) => {
                state.error = action.payload ? action.payload.error : null;
                state.loading = false;
            })
            .addCase(getAdPlan.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAdPlan.fulfilled, (state, action) => {
                state.adPlans = action.payload;
                state.loading = false;
            })
            .addCase(getAdPlan.rejected, (state, action) => {
                state.error = action.payload?.error || null;
                state.loading = false;
            })
            .addCase(createAd.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createAd.fulfilled, (state, action) => {
                state.data = action.payload;
                state.loading = false;
            })
            .addCase(createAd.rejected, (state, action) => {
                state.error = action.payload.error || null;
                state.loading = false;
            })
            .addCase(updateAd.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateAd.fulfilled, (state, action) => {
                state.data = action.payload;
                state.loading = false;
            })
            .addCase(updateAd.rejected, (state, action) => {
                state.error = action.payload.error || null;
                state.loading = false;
            })
            .addCase(getAnalyticsCount.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAnalyticsCount.fulfilled, (state, action) => {
                console.log('Analytics data stored in reducer:', action.payload);
                state.analyticsCount = action.payload;
                state.loading = false;
            })
            .addCase(getAnalyticsCount.rejected, (state, action) => {
                state.error = action.payload?.error || null;
                state.loading = false;
            })
            .addCase(getBannerAds.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getBannerAds.fulfilled, (state, action) => {
                state.bannerAds = action.payload;
                state.loading = false;
            })
            .addCase(getBannerAds.rejected, (state, action) => {
                state.error = action.payload?.error || null;
                state.loading = false;
            })
    }
});

export const {
    updateProfileCache,
    clearProfileCache,
    updateDashboardAdsCache,
    clearDashboardAdsCache,
    updateProfileInCache,
    removeProfileFromCache
} = adsSlice.actions;

export default adsSlice.reducer;